import React, { useEffect, useState } from "react";
import axios from "axios";
import { PayPalScriptProvider, PayPalButtons, FUNDING } from "@paypal/react-paypal-js";
import ApplePayButton from "../components/ApplePayButton";
import { toast } from "sonner";
import { Zap, Gift, Trash2, ShieldCheck, Sparkles, Apple, CreditCard, Smartphone, Wallet, History, ArrowLeft } from "lucide-react";
import BrandedReceipt from "../components/BrandedReceipt";
import LoadBonusPromo from "../components/LoadBonusPromo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// All funding methods PayPal makes available at checkout. Shown up-front so
// shoppers know Apple Pay / Google Pay / Venmo etc. are options before they click.
const PAY_METHODS = [
  { key: "paypal", label: "PayPal", bg: "#ffc439", fg: "#003087", icon: Wallet },
  { key: "applepay", label: "Apple\u00A0Pay", bg: "#000000", fg: "#ffffff", icon: Apple },
  { key: "googlepay", label: "Google\u00A0Pay", bg: "#ffffff", fg: "#3c4043", icon: Smartphone },
  { key: "card", label: "Debit / Credit", bg: "#2b2f36", fg: "#ffffff", icon: CreditCard },
  { key: "cashapp", label: "Cash\u00A0App", bg: "#00D632", fg: "#ffffff", icon: CreditCard },
  { key: "chime", label: "Chime", bg: "#1EC677", fg: "#ffffff", icon: CreditCard },
];

// Map each tile to the PayPal funding button it should launch.
// Card-based methods (card/cashapp/chime) open the card form directly — no
// PayPal login or email needed. Apple/Google Pay surface inside the PayPal
// window on supported devices.
const METHOD_TO_FUNDING = {
  paypal: FUNDING.PAYPAL,
  applepay: FUNDING.PAYPAL,
  googlepay: FUNDING.PAYPAL,
  card: FUNDING.CARD,
  cashapp: FUNDING.CARD,
  chime: FUNDING.CARD,
};
const METHOD_HINT = {
  paypal: "You'll finish in the PayPal window.",
  applepay: "Apple Pay shows up inside the PayPal window on Apple devices (Safari).",
  googlepay: "Google Pay shows up inside the PayPal window on Chrome / Android.",
  card: "Enter any debit or credit card — no PayPal account or email needed.",
  cashapp: "Enter your Cash App card number — no PayPal account or email needed.",
  chime: "Enter your Chime card number — no PayPal account or email needed.",
};

function PayMethodsStrip({ selected, onSelect }) {
  return (
    <div data-testid="bc-methods-strip" style={{ marginTop: 16 }}>
      <div style={{ fontSize: 12, letterSpacing: "0.08em", textTransform: "uppercase", opacity: 0.7, marginBottom: 8, fontWeight: 700 }}>
        Tap how you'd like to pay
      </div>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
        {PAY_METHODS.map((m) => {
          const Icon = m.icon;
          const active = selected === m.key;
          return (
            <button key={m.key} type="button" data-testid={`bc-method-${m.key}`}
              onClick={() => onSelect(m.key)}
              style={{
                display: "inline-flex", alignItems: "center", gap: 6, padding: "8px 14px",
                borderRadius: 999, background: m.bg, color: m.fg, fontWeight: 800, fontSize: 13,
                cursor: "pointer",
                border: active ? "2px solid #ffd67a" : (m.key === "googlepay" ? "1px solid #dadce0" : "1px solid rgba(255,255,255,0.12)"),
                boxShadow: active ? "0 0 0 3px rgba(255,214,122,0.35), 0 2px 8px rgba(0,0,0,0.25)" : "0 2px 8px rgba(0,0,0,0.25)",
                transform: active ? "translateY(-1px)" : "none", transition: "all .15s ease",
              }}>
              <Icon size={15} strokeWidth={2.4} /> {m.label}
            </button>
          );
        })}
      </div>
      <div style={{ fontSize: 11.5, opacity: 0.6, marginTop: 8 }}>
        Apple&nbsp;Pay & Google&nbsp;Pay appear inside the PayPal window on supported devices &amp; browsers.
      </div>
      <div data-testid="bc-card-note" style={{
        fontSize: 12.5, marginTop: 8, padding: "8px 12px", borderRadius: 10,
        background: "rgba(255,215,122,0.08)", border: "1px solid rgba(255,215,122,0.22)", color: "#ffe6a8",
      }}>
        <b>Cash&nbsp;App or Chime?</b> Those cards work like any Visa/Mastercard — pick them (or “Debit / Credit”) to enter the card directly, no PayPal login.
      </div>
    </div>
  );
}

export default function BuyCredits() {
  const [packages, setPackages] = useState([]);
  const [cfg, setCfg] = useState(null);
  const [selected, setSelected] = useState(null);
  const [balance, setBalance] = useState(null);
  const [promo, setPromo] = useState(null);
  const [saved, setSaved] = useState([]);
  const [idToken, setIdToken] = useState(null);
  const [saveMethod, setSaveMethod] = useState(false);
  const [payMethod, setPayMethod] = useState("paypal");
  const [busyVault, setBusyVault] = useState(null);
  const [history, setHistory] = useState([]);
  const [customAmt, setCustomAmt] = useState("");
  const [wheelWin, setWheelWin] = useState(null);
  const [depositReceipt, setDepositReceipt] = useState(null);

  const CUSTOM_MIN = 5;
  const CUSTOM_MAX = 500;
  const customValid = (() => {
    const n = Math.floor(Number(customAmt));
    return Number.isFinite(n) && String(customAmt).trim() !== "" && n >= CUSTOM_MIN && n <= CUSTOM_MAX;
  })();
  const applyCustomAmount = () => {
    const n = Math.floor(Number(customAmt));
    if (!customValid) {
      toast.error(`Enter a whole-dollar amount between $${CUSTOM_MIN} and $${CUSTOM_MAX}`);
      return;
    }
    setSelected({ id: "custom", price_usd: `${n}.00`, credits: n });
    setTimeout(() => {
      const el = document.querySelector('[data-testid="bc-paypal-area"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const selectMethod = (key) => {
    setPayMethod(key);
    setTimeout(() => {
      const el = document.querySelector('[data-testid="bc-paypal-area"]');
      if (el) el.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 60);
  };

  const load = async () => {
    try {
      const [p, c, me, pr, sm, tok, hist] = await Promise.all([
        axios.get(`${API}/payments/packages`),
        axios.get(`${API}/payments/config`),
        axios.get(`${API}/auth/me`).catch(() => null),
        axios.get(`${API}/payments/promo`).catch(() => null),
        axios.get(`${API}/payments/saved-methods`).catch(() => null),
        axios.get(`${API}/payments/paypal/id-token`).catch(() => null),
        axios.get(`${API}/payments/history`).catch(() => null),
      ]);
      setPackages(p.data.packages);
      setCfg(c.data);
      if (me?.data) setBalance(me.data.credits);
      if (pr?.data) setPromo(pr.data);
      if (sm?.data) setSaved(sm.data.methods || []);
      if (tok?.data) setIdToken(tok.data.id_token || null);
      if (hist?.data) setHistory(hist.data.items || []);
    } catch (e) { toast.error("Failed to load packages"); }
  };

  useEffect(() => { load(); }, []);

  const fmt = (n) => Number(n).toLocaleString(undefined, { minimumFractionDigits: 0 });

  const bonusFor = (pkg) => {
    if (!promo?.first_load || !pkg) return 0;
    return pkg.id === promo.bonus_package ? (promo.bonus_credits || 0) : 0;
  };

  const applyCapture = (res) => {
    setBalance(res.data.new_balance);
    const bonus = res.data.bonus_added || 0;
    if (bonus > 0) {
      toast.success(`🎉 First-load bonus! Added ${fmt(res.data.credits_added)} + ${fmt(bonus)} FREE credits. Balance: ${fmt(res.data.new_balance)}`);
    } else {
      toast.success(`Added ${fmt(res.data.credits_added)} credits! New balance: ${fmt(res.data.new_balance)}`);
    }
    const wheel = res.data.wheel;
    if (wheel && wheel.tickets > 0) {
      setWheelWin(wheel);
      toast.success(`🎡 You earned ${fmt(wheel.tickets)} ${wheel.tickets === 1 ? "ticket" : "tickets"} in "${wheel.name}"!`, { duration: 6000 });
    }
    // Offer a branded, shareable deposit receipt for the just-completed load.
    const credited = Number(res.data.credits_added || 0) + Number(bonus || 0);
    setDepositReceipt({
      kind: "deposit",
      gross: Number(res.data.credits_added || 0),
      fee: 0,
      net: credited,
      method: "PayPal",
      handle: "",
      etaHours: null,
      afterHours: false,
      date: new Date().toISOString(),
      reference: String(res.data.transaction_id || res.data.order_id || Date.now()).slice(-8),
    });
    setSelected(null);
    load();
  };

  const payWithSaved = async (pkg, vaultId) => {
    if (busyVault) return;
    setBusyVault(vaultId);
    try {
      const { data } = await axios.post(`${API}/payments/paypal/create-order`, { package_id: pkg.id, custom_amount: pkg.id === "custom" ? pkg.credits : undefined, vault_id: vaultId, origin: window.location.origin });
      const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.id });
      applyCapture(res);
    } catch (e) {
      toast.error("1-click payment failed — try the PayPal button below.");
    } finally { setBusyVault(null); }
  };

  const deleteSaved = async (vaultId) => {
    try {
      await axios.post(`${API}/payments/saved-methods/delete`, { order_id: vaultId });
      setSaved((s) => s.filter((m) => m.vault_id !== vaultId));
      toast.success("Saved method removed");
    } catch { toast.error("Could not remove method"); }
  };

  const scriptOptions = cfg?.client_id
    ? {
        clientId: cfg.client_id,
        currency: cfg.currency || "USD",
        intent: "capture",
        components: "buttons,applepay",
        disableFunding: "paylater,credit",
      }
    : null;

  const promoBonus = selected ? bonusFor(selected) : 0;

  return (
    <div style={{ minHeight: "100vh", background: "radial-gradient(120% 90% at 50% 0%, #1a0f06, #0a0705)", color: "#f3e6c8", padding: "32px 16px" }} data-testid="buy-credits-page">
      <div style={{ maxWidth: 820, margin: "0 auto" }}>
        <button onClick={() => window.location.assign('/lobby')} data-testid="bc-back-to-lobby"
          style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 14, cursor: "pointer",
            background: "rgba(255,255,255,0.06)", color: "#ffd67a", fontWeight: 700, fontSize: 14,
            padding: "8px 16px", borderRadius: 999, border: "1px solid rgba(255,215,122,0.3)" }}>
          <ArrowLeft size={16} /> Back to Lobby
        </button>
        <h1 style={{ fontSize: 34, fontWeight: 800, color: "#ffd67a", letterSpacing: "0.02em" }}>Buy Credits</h1>
        {balance != null && (
          <p style={{ opacity: 0.85 }} data-testid="bc-balance">Current balance: <b style={{ color: "#ffd67a" }}>{fmt(balance)}</b> credits</p>
        )}
        <p style={{ opacity: 0.7, fontSize: 14, marginTop: 4 }}>1 credit = $1.00 USD · secure checkout via PayPal</p>

        {/* Wheel-ticket confirmation after a load */}
        {wheelWin && wheelWin.tickets > 0 && (
          <div data-testid="bc-wheel-ticket-banner" style={{
            marginTop: 18, borderRadius: 16, padding: "16px 20px", position: "relative", overflow: "hidden",
            background: "linear-gradient(100deg,#7a2ff2,#b14dff 55%,#ffd67a)", color: "#1a0630",
            boxShadow: "0 10px 34px rgba(150,60,255,0.45)", border: "2px solid #ffe6a8",
            display: "flex", alignItems: "center", gap: 14,
          }}>
            <span style={{ fontSize: 40, lineHeight: 1 }} aria-hidden>🎡</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 20, fontWeight: 900, textTransform: "uppercase", letterSpacing: "0.01em" }}>
                You're in the draw!
              </div>
              <div style={{ fontSize: 15, fontWeight: 800, marginTop: 2 }}>
                You earned <b>{fmt(wheelWin.tickets)}</b> {wheelWin.tickets === 1 ? "ticket" : "tickets"} in
                {" "}<b>“{wheelWin.name}”</b> from this load. Good luck! 🍀
              </div>
            </div>
            <button data-testid="bc-wheel-ticket-dismiss" onClick={() => setWheelWin(null)}
              style={{ background: "rgba(0,0,0,0.18)", border: "none", color: "#1a0630", fontWeight: 900,
                borderRadius: 999, width: 30, height: 30, cursor: "pointer", fontSize: 16, flexShrink: 0 }}>
              ×
            </button>
          </div>
        )}

        <PayMethodsStrip selected={payMethod} onSelect={selectMethod} />

        {/* Recurring load-to-get bonus (tiers + live countdown) */}
        <LoadBonusPromo />


        {/* Daily promo banner */}
        {promo?.first_load && (
          <div data-testid="bc-promo-banner" style={{
            marginTop: 20, borderRadius: 16, padding: "18px 22px", position: "relative", overflow: "hidden",
            background: "linear-gradient(100deg,#ff4d2e,#ff8c00 55%,#ffd67a)", color: "#2a0f00",
            boxShadow: "0 10px 34px rgba(255,120,30,0.45)", border: "2px solid #ffe6a8",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Gift size={34} strokeWidth={2.4} />
              <div>
                <div style={{ fontSize: 22, fontWeight: 900, letterSpacing: "0.01em", textTransform: "uppercase" }}>
                  Daily · First Load Bonus 🔥
                </div>
                <div style={{ fontSize: 15, fontWeight: 700, marginTop: 2 }}>
                  Load $10, get $10 FREE on your first purchase — that's 20 credits!
                </div>
              </div>
            </div>
            <Sparkles size={120} style={{ position: "absolute", right: -18, top: -18, opacity: 0.18 }} />
          </div>
        )}

        {/* Saved methods — 1-click checkout */}
        {saved.length > 0 && (
          <div style={{ marginTop: 24 }} data-testid="bc-saved-methods">
            <h2 style={{ fontSize: 17, fontWeight: 800, color: "#ffd67a", display: "flex", alignItems: "center", gap: 8 }}>
              <Zap size={18} /> Saved payment methods — 1-click checkout
            </h2>
            <p style={{ opacity: 0.6, fontSize: 13, margin: "2px 0 10px" }}>
              {selected ? `Charging ${fmt(selected.credits)} credits ($${Number(selected.price_usd).toFixed(2)})` : "Select a package above, then tap Pay instantly."}
            </p>
            {saved.map((m) => (
              <div key={m.vault_id} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 14px", marginBottom: 8, borderRadius: 12, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,215,122,0.25)" }}>
                <span style={{ flex: 1, fontWeight: 700 }}>{m.label}</span>
                <button
                  data-testid={`bc-pay-saved-${m.vault_id}`}
                  disabled={!selected || busyVault === m.vault_id}
                  onClick={() => payWithSaved(selected, m.vault_id)}
                  style={{
                    cursor: !selected ? "not-allowed" : "pointer", opacity: !selected ? 0.5 : 1,
                    padding: "8px 16px", borderRadius: 999, border: "none", fontWeight: 800, color: "#2a0f00",
                    background: "linear-gradient(135deg,#ffd67a,#ff8c00)",
                  }}>
                  {busyVault === m.vault_id ? "Processing…" : "Pay instantly"}
                </button>
                <button aria-label="Remove" data-testid={`bc-delete-saved-${m.vault_id}`} onClick={() => deleteSaved(m.vault_id)}
                  style={{ background: "none", border: "none", cursor: "pointer", color: "#ff9d8a" }}>
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        )}

        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 14, marginTop: 24 }}>
          {packages.map((pkg) => {
            const active = selected?.id === pkg.id;
            const bonus = bonusFor(pkg);
            return (
              <button key={pkg.id} data-testid={`bc-package-${pkg.id}`} onClick={() => setSelected(pkg)}
                style={{
                  position: "relative", textAlign: "left", cursor: "pointer", padding: "18px 20px", borderRadius: 14,
                  background: active ? "linear-gradient(135deg,#3a2408,#241606)" : "rgba(255,255,255,0.04)",
                  border: active ? "2px solid #ffb347" : "1px solid rgba(255,215,122,0.25)",
                  boxShadow: active ? "0 8px 26px rgba(255,140,40,0.25)" : "none", transition: "all .18s ease",
                }}>
                {bonus > 0 && (
                  <span style={{ position: "absolute", top: 10, right: 10, fontSize: 11, fontWeight: 800, color: "#2a0f00", background: "#39d98a", padding: "2px 8px", borderRadius: 999 }}>
                    +{fmt(bonus)} FREE
                  </span>
                )}
                <div style={{ fontSize: 26, fontWeight: 800, color: "#ffd67a" }}>${Number(pkg.price_usd).toFixed(2)}</div>
                <div style={{ fontSize: 15, opacity: 0.85, marginTop: 4 }}>{fmt(pkg.credits)} credits</div>
                {bonus > 0 && (
                  <div style={{ fontSize: 13, color: "#7bf0b0", marginTop: 4, fontWeight: 700 }}>{fmt(pkg.credits + bonus)} total credits</div>
                )}
              </button>
            );
          })}
        </div>

        {/* Custom "pay what you want" amount */}
        <div data-testid="bc-custom-amount" style={{
          marginTop: 14, padding: "16px 18px", borderRadius: 14,
          background: selected?.id === "custom" ? "linear-gradient(135deg,#3a2408,#241606)" : "rgba(255,255,255,0.04)",
          border: selected?.id === "custom" ? "2px solid #ffb347" : "1px solid rgba(255,215,122,0.25)",
          transition: "all .18s ease",
        }}>
          <div style={{ fontSize: 14, fontWeight: 800, color: "#ffd67a", marginBottom: 10 }}>
            Or enter a custom amount
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", flex: "1 1 200px", minWidth: 160 }}>
              <span style={{ position: "absolute", left: 14, top: "50%", transform: "translateY(-50%)", fontSize: 18, fontWeight: 800, color: "#ffd67a" }}>$</span>
              <input
                data-testid="bc-custom-input"
                type="number" inputMode="numeric" min={CUSTOM_MIN} max={CUSTOM_MAX} step={1}
                value={customAmt}
                onChange={(e) => setCustomAmt(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") applyCustomAmount(); }}
                placeholder={`${CUSTOM_MIN} – ${CUSTOM_MAX}`}
                style={{
                  width: "100%", boxSizing: "border-box", padding: "12px 12px 12px 30px",
                  borderRadius: 10, background: "rgba(0,0,0,0.35)", color: "#ffe6a8",
                  border: "1px solid rgba(255,215,122,0.35)", fontSize: 18, fontWeight: 800, outline: "none",
                }}
              />
            </div>
            <button
              data-testid="bc-custom-use"
              type="button" onClick={applyCustomAmount} disabled={!customValid}
              style={{
                padding: "12px 20px", borderRadius: 999, border: "none", fontWeight: 800, fontSize: 14,
                cursor: customValid ? "pointer" : "not-allowed", opacity: customValid ? 1 : 0.5,
                color: "#2a0f00", background: "linear-gradient(135deg,#ffd67a,#ff8c00)", whiteSpace: "nowrap",
              }}>
              {customValid ? `Buy ${fmt(Math.floor(Number(customAmt)))} credits` : "Use this amount"}
            </button>
          </div>
          <div style={{ fontSize: 12, opacity: 0.65, marginTop: 8 }}>
            Whole dollars only · ${CUSTOM_MIN}–${CUSTOM_MAX} · 1 credit = $1.00 (bonus applies to the $10 pack only)
          </div>
        </div>

        <div style={{ marginTop: 28, minHeight: 60 }} data-testid="bc-paypal-area">
          {selected && scriptOptions ? (
            <PayPalScriptProvider options={scriptOptions}>
              <div style={{ maxWidth: 460 }}>
                <p style={{ marginBottom: 12 }}>
                  Paying <b style={{ color: "#ffd67a" }}>${Number(selected.price_usd).toFixed(2)}</b> for <b>{fmt(selected.credits)}</b> credits
                  {promoBonus > 0 && <span style={{ color: "#7bf0b0", fontWeight: 700 }}> + {fmt(promoBonus)} FREE 🎁</span>}
                </p>
                <p style={{ fontSize: 12.5, opacity: 0.7, marginBottom: 14 }}>
                  Choose <b style={{ color: "#ffd67a" }}>PayPal</b> to pay with your PayPal balance, Apple&nbsp;Pay, Google&nbsp;Pay or Venmo — or <b style={{ color: "#ffd67a" }}>Debit or Credit Card</b> to pay with any card (Cash&nbsp;App / Chime cards work here too), no PayPal account needed.
                </p>
                <ApplePayButton pkg={selected} onSuccess={applyCapture} />
                <PayPalButtons
                  key={selected.id}
                  style={{ layout: "vertical", color: "gold", shape: "pill", label: "paypal" }}
                  createOrder={async () => {
                    const { data } = await axios.post(`${API}/payments/paypal/create-order`, {
                      package_id: selected.id,
                      custom_amount: selected.id === "custom" ? selected.credits : undefined,
                      save: false,
                      origin: window.location.origin,
                    });
                    return data.id;
                  }}
                  onApprove={async (data) => {
                    try {
                      const res = await axios.post(`${API}/payments/paypal/capture`, { order_id: data.orderID });
                      applyCapture(res);
                    } catch (e) { toast.error("Payment capture failed"); }
                  }}
                  onError={() => toast.error("Payment error — please try another option")}
                />
              </div>
            </PayPalScriptProvider>
          ) : (
            <p style={{ opacity: 0.6 }}>Select a package to continue.</p>
          )}
        </div>

        {/* Purchase history — recent top-ups */}
        {history.length > 0 && (
          <div style={{ marginTop: 40 }} data-testid="bc-history">
            <h2 style={{ fontSize: 17, fontWeight: 800, color: "#ffd67a", display: "flex", alignItems: "center", gap: 8, marginBottom: 10 }}>
              <History size={18} /> Your recent top-ups
            </h2>
            <div style={{ borderRadius: 12, overflow: "hidden", border: "1px solid rgba(255,215,122,0.18)" }}>
              {history.map((h, i) => (
                <div key={i} data-testid={`bc-history-row-${i}`}
                  style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px",
                    background: i % 2 ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.06)" }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 800 }}>
                      +{fmt(h.credits)} credits
                      {h.bonus > 0 && <span style={{ color: "#7bf0b0", fontWeight: 700 }}> + {fmt(h.bonus)} bonus</span>}
                    </div>
                    <div style={{ fontSize: 12, opacity: 0.6 }}>
                      {h.date ? new Date(h.date).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" }) : ""}
                    </div>
                  </div>
                  <div style={{ fontWeight: 800, color: "#ffd67a" }}>${Number(h.amount_usd || 0).toFixed(2)}</div>
                  <span style={{ fontSize: 11, fontWeight: 800, color: "#2a0f00", background: "#39d98a", padding: "2px 8px", borderRadius: 999 }}>Paid</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <BrandedReceipt
        open={!!depositReceipt}
        onClose={() => setDepositReceipt(null)}
        receipt={depositReceipt}
      />
    </div>
  );
}
